import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authApi, bindAuth } from '@/api';
import type { AccessTokenClaims, LoginRequest } from '@/types/api';
import type { UserRole } from '@/types/enums';
import { getRefreshToken, setRefreshToken } from '@/lib/tokens';

interface AuthUser {
  username: string;
  role: UserRole;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function decodeJwt(token: string): AccessTokenClaims | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = atob(padded);
    const json = decodeURIComponent(
      Array.from(decoded)
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    return JSON.parse(json) as AccessTokenClaims;
  } catch {
    return null;
  }
}

/**
 * @throws Error si les claims sont absentes/illisibles ou si `role` manque.
 * Un fallback silencieux `?? 'USER'` masquerait une régression backend (admin
 * devenant silencieusement USER) → on force un logout explicite.
 */
function userFromAccess(token: string | null): AuthUser | null {
  if (!token) return null;
  const claims = decodeJwt(token);
  if (!claims) throw new Error('JWT illisible');
  if (!claims.role) throw new Error('JWT sans claim "role"');
  if (!claims.sub) throw new Error('JWT sans claim "sub"');
  return { username: claims.sub, role: claims.role };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [accessToken, setAccessTokenState] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  const setAccess = useCallback((token: string | null) => {
    setAccessTokenState(token);
  }, []);

  const onFailure = useCallback(() => {
    queryClient.clear();
    setAccessTokenState(null);
    setRefreshToken(null);
    navigate('/login', { replace: true });
  }, [navigate, queryClient]);

  // Inject getters/setters into axios interceptor.
  useEffect(() => {
    bindAuth({
      getAccess: () => accessToken,
      setAccess,
      onFailure,
    });
  }, [accessToken, setAccess, onFailure]);

  // Boot: if a refresh token is persisted, try to exchange it for a fresh access.
  useEffect(() => {
    let cancelled = false;

    const refresh = getRefreshToken();
    if (!refresh) {
      setIsInitializing(false);
      return;
    }

    void (async () => {
      try {
        const tokens = await authApi.refresh({ refreshToken: refresh });
        if (cancelled) return;
        setRefreshToken(tokens.refreshToken);
        setAccessTokenState(tokens.accessToken);
      } catch {
        if (cancelled) return;
        setRefreshToken(null);
      } finally {
        if (!cancelled) setIsInitializing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (credentials: LoginRequest) => {
      const tokens = await authApi.login(credentials);
      queryClient.clear();
      setRefreshToken(tokens.refreshToken);
      setAccessTokenState(tokens.accessToken);
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    queryClient.clear();
    setRefreshToken(null);
    setAccessTokenState(null);
    navigate('/login', { replace: true });
  }, [navigate, queryClient]);

  const user = useMemo(() => {
    try {
      return userFromAccess(accessToken);
    } catch (e) {
      if (import.meta.env.DEV) {
        console.error('Token invalide, logout forcé :', e);
      }
      // Schedule logout hors du render pour éviter la boucle setState.
      queueMicrotask(() => onFailure());
      return null;
    }
  }, [accessToken, onFailure]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!accessToken,
      isInitializing,
      login,
      logout,
    }),
    [user, accessToken, isInitializing, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
